import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import React, { ReactElement, useEffect, useState } from 'react';
import { AISystemsService } from 'services/AISystemService';
import { useNavigate } from 'react-router-dom';

interface InventoryRowProps {
  label: string;
  value: number;
}

const InventoryRow = ({
  label,
  value,
}: InventoryRowProps): React.ReactElement => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    className="inventory-row"
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      className="inventory-row-label-group"
    >
      <Typography className="inventory-row-label">{label}</Typography>
    </Stack>
    <Typography className="inventory-row-value">{value}</Typography>
  </Stack>
);

const AIInventory = (): ReactElement => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({
    pendingCount: 0,
    approvedCount: 0,
    publicPendingCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  // API Calls
  const loadSummary = (): void => {
    setIsLoading(true);
    AISystemsService.getSummaryCount()
      .then((response) => {
        const { data } = response.data;
        setSummary(data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Hooks
  useEffect(() => {
    loadSummary();
  }, []);

  const { approvedCount, pendingCount, publicPendingCount } = summary;

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title=" AI System Approvals"
        subheader="Overview of AI system approvals"
        sx={{
          '& .MuiCardHeader-title': {
            fontSize: '18px',
            fontWeight: 600,
            color: '#052921',
          },
          '& .MuiCardHeader-subheader': {
            fontSize: '14px',
            color: '#6b7280',
          },
        }}
      />

      <CardContent>
        <Stack>
          <Card variant="outlined" sx={{ borderRadius: '8px' }}>
            <CardHeader
              className="inventory-card-header"
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#052921',
                    }}
                  >
                    AI Systems
                  </Typography>
                </Stack>
              }
              action={
                <IconButton
                  onClick={() => {
                    navigate(`/ai-systems`, {
                      state: { activeTab: '/ai-systems' },
                    });
                  }}
                >
                  <RemoveRedEyeIcon />
                </IconButton>
              }
              sx={{ pb: 0 }}
            />

            <CardContent>
              <Stack direction="column" gap={1}>
                <InventoryRow
                  label="AIs awaiting approval"
                  value={pendingCount}
                />
                <InventoryRow label="Approved" value={approvedCount} />
                <InventoryRow
                  label="Public submissions pending"
                  value={publicPendingCount}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AIInventory;
